<script setup lang="ts">
import { useModalStore } from "../stores/modalStore.ts";
import Button from "./Button.vue";

/**
 * Ready-made modal content component with a draggable header, scrollable body,
 * and an optional OK / Cancel footer.
 *
 * Use as the `content` argument to `modalStore.show()`, passing props via
 * `componentProps`. The ModalContainer handles drag, resize, and Escape; this
 * component owns the visual chrome (header title, × close, footer buttons).
 *
 * Example:
 *   modalStore.show(OKCancelModal, {
 *     width: 440,
 *     componentProps: {
 *       title: "Confirm",
 *       message: "Are you sure?",
 *       okButtonText: "Yes",
 *       cancelButtonText: "No",
 *     },
 *   });
 */
const props = withDefaults(
  defineProps<{
    /** Text shown in the modal header. */
    title?: string;
    /**
     * Plain-text or HTML message rendered in the body when no slot is provided.
     * Prefer passing a component with a slot for rich content.
     */
    message?: string;
    /** Label for the primary (OK) button. Omit to hide. */
    okButtonText?: string;
    /** Label for the secondary (Cancel) button. Omit to hide. */
    cancelButtonText?: string;
    /** Called before the modal closes when OK is clicked. */
    onOk?: () => void | Promise<void>;
    /** Called before the modal closes when Cancel or × is clicked. */
    onCancel?: () => void | Promise<void>;
    /**
     * Alignment of footer buttons.
     * Default: 'end' (right-aligned).
     */
    footerAlign?: "start" | "center" | "end";
  }>(),
  {
    title: "",
    footerAlign: "end",
  }
);

const modalStore = useModalStore();

async function handleOk(): Promise<void> {
  if (props.onOk) await props.onOk();
  modalStore.close();
}

async function handleCancel(): Promise<void> {
  if (props.onCancel) await props.onCancel();
  modalStore.close();
}

function handleXClose(): void {
  // X close has the same behaviour as Cancel (no OK side-effects)
  if (props.onCancel) void props.onCancel();
  modalStore.close();
}
</script>

<template>
  <!--
    Wrapper fills modal-window (display:flex; flex-direction:column).
    Add class "draggable" to the header so ModalContainer's drag handler picks it up.
    If the parent modal was opened with draggable:false the handler exits early, so
    the class is harmless.
  -->
  <div class="ok-cancel-layout">
    <div class="modal-header draggable">
      <span class="modal-header-title">{{ title }}</span>
      <button
        class="modal-header-close"
        aria-label="Close"
        @mousedown.stop
        @click.stop="handleXClose"
      >
        &times;
      </button>
    </div>

    <div class="modal-body">
      <!-- Rich content via default slot; falls back to the message prop -->
      <slot>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="message" v-html="message" />
      </slot>
    </div>

    <div
      v-if="okButtonText || cancelButtonText"
      class="modal-footer"
      :style="{ justifyContent: footerAlign }"
    >
      <Button v-if="cancelButtonText" variant="secondary" @click="handleCancel">
        {{ cancelButtonText }}
      </Button>
      <Button v-if="okButtonText" variant="primary" @click="handleOk">
        {{ okButtonText }}
      </Button>
    </div>
  </div>
</template>
